import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Sparkles, ShoppingBag, Check, Minus, Plus, Loader2, MessageCircle, ShieldCheck, ChevronRight,
} from "lucide-react";
import {
  fetchProductByTitle,
  addLineToSharedCart,
  money,
} from "@/lib/shopify";
import { waUrl } from "@/lib/brand";

/**
 * Reusable Shopify product card.
 * Props:
 *  - title        (string) exact Shopify product title to fetch
 *  - features     (string[]) bullet feature list
 *  - weightLabel  (string) e.g. "500 g"
 *  - tag          (string) badge above title
 *  - notifyMsg    (string) WhatsApp message when out of stock
 *  - buyTestId    (string) data-testid for Buy Now button
 *  - addTestId    (string) data-testid for Add to Cart button
 *  - reverse      (bool)  reverse image/details layout
 *  - cardTestId   (string) data-testid for the card wrapper
 */
export default function ShopifyProductCard({
  title,
  features = [],
  weightLabel = "500 g",
  tag = "Live on Shopify",
  notifyMsg,
  buyTestId,
  addTestId,
  reverse = false,
  cardTestId,
  detailsTo,
  detailsLabel,
}) {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qty, setQty] = useState(1);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetchProductByTitle(title)
      .then((p) => mounted && setProduct(p))
      .catch((e) => mounted && setError(e.message))
      .finally(() => mounted && setLoading(false));
    return () => (mounted = false);
  }, [title]);

  const inc = () => setQty((q) => Math.min(q + 1, 20));
  const dec = () => setQty((q) => Math.max(q - 1, 1));

  const onBuyNow = async () => {
    if (!product || !product.firstVariant) return;
    setBuying(true);
    try {
      const cart = await addLineToSharedCart(product.firstVariant.id, qty);
      if (!cart || !cart.checkoutUrl) throw new Error("Checkout URL not returned by Shopify.");
      window.location.assign(cart.checkoutUrl);
    } catch (e) {
      toast.error(e.message || "Unable to start checkout. Please try again.");
      setBuying(false);
    }
  };

  const onAddToCart = async () => {
    if (!product || !product.firstVariant) return;
    setBuying(true);
    try {
      const cart = await addLineToSharedCart(product.firstVariant.id, qty);
      toast.success(`Added ${qty} × ${shortTitle(product.title)} to your cart`, {
        action: {
          label: "Checkout",
          onClick: () => window.location.assign(cart.checkoutUrl),
        },
      });
    } catch (e) {
      toast.error(e.message || "Unable to add to cart.");
    } finally {
      setBuying(false);
    }
  };

  if (loading) return <ShopifyCardSkeleton reverse={reverse} testId={cardTestId} />;

  if (error || !product) {
    return (
      <div className="card-premium p-8 text-center" data-testid={cardTestId}>
        <div className="text-[#8D6E63] text-sm uppercase tracking-widest font-bold mb-2">Shopify</div>
        <div className="font-display text-xl text-[#143C18] mb-2">Product unavailable</div>
        <p className="text-[#5D4037] text-sm">{error || "Could not load product from Shopify."}</p>
      </div>
    );
  }

  const img = product.featuredImage || product.images[0];
  const isSale =
    product.compareAtPrice &&
    Number(product.compareAtPrice.amount) > Number(product.price.amount);
  const available = product.availableForSale && (product.firstVariant?.availableForSale ?? true);

  return (
    <div className="card-premium overflow-hidden" data-testid={cardTestId}>
      <div className={`grid lg:grid-cols-2 ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
        {/* IMAGE */}
        <div className="relative bg-gradient-to-br from-[#FBF4E1] to-[#F6EBCB] p-6 lg:p-10 flex items-center justify-center min-h-[320px]">
          <AnimatePresence mode="wait">
            <motion.img
              key={img?.url}
              src={img?.url}
              alt={img?.altText || product.title}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="w-full max-w-[420px] rounded-2xl object-cover shadow-[0_25px_50px_-15px_rgba(20,60,24,0.35)]"
              loading="lazy"
              data-testid={`${cardTestId}-image`}
            />
          </AnimatePresence>
          <div className="absolute top-5 left-5 chip chip-gold shadow-sm">
            <Sparkles className="w-3 h-3" /> {tag}
          </div>
          {isSale && (
            <div className="absolute top-5 right-5 chip bg-[#2E7D32] !text-white border-transparent">
              Sale
            </div>
          )}
        </div>

        {/* DETAILS */}
        <div className="p-6 lg:p-10 flex flex-col justify-center">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8D6E63] font-bold mb-3">
            Rudrima Signature · Shopify
          </div>
          <h3
            className="font-display text-3xl lg:text-4xl font-bold text-[#143C18] leading-tight"
            data-testid={`${cardTestId}-title`}
          >
            {product.title}
          </h3>
          {product.description && (
            <p className="mt-3 text-[#5D4037] leading-relaxed line-clamp-3" data-testid={`${cardTestId}-description`}>
              {product.description}
            </p>
          )}

          {features.length > 0 && (
            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {features.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-[#1A1614]">
                  <span className="w-5 h-5 rounded-full bg-[#2E7D32]/10 flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3 text-[#2E7D32]" strokeWidth={3} />
                  </span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Availability */}
          <div className="mt-5 flex items-center gap-2 text-sm flex-wrap" data-testid={`${cardTestId}-availability`}>
            {available ? (
              <span className="inline-flex items-center gap-1.5 text-[#1F5D25] font-semibold">
                <span className="w-2 h-2 rounded-full bg-[#2E7D32] animate-pulse"></span> In Stock — Ready to ship
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-[#8D6E63] font-semibold">
                <span className="w-2 h-2 rounded-full bg-[#8D6E63]"></span> Out of Stock
              </span>
            )}
            <span className="text-[#8D6E63]">·</span>
            <span className="text-[#8D6E63] inline-flex items-center gap-1"><ShieldCheck className="w-4 h-4 text-[#2E7D32]" /> Secure Shopify Checkout</span>
          </div>

          {/* Price + Qty */}
          <div className="mt-6 flex flex-wrap items-end justify-between gap-4 pt-6 border-t border-[#8D6E63]/15">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#8D6E63] font-bold">{weightLabel}</div>
              <div className="font-display text-3xl font-bold text-[#143C18] flex items-baseline gap-3" data-testid={`${cardTestId}-price`}>
                {money(product.price.amount, product.currency)}
                {isSale && (
                  <span className="text-base font-body font-medium text-[#8D6E63] line-through">
                    {money(product.compareAtPrice.amount, product.compareAtPrice.currencyCode)}
                  </span>
                )}
              </div>
            </div>

            {/* Quantity */}
            <div className="flex items-center gap-2" data-testid={`${cardTestId}-qty-selector`}>
              <div className="text-xs uppercase tracking-widest text-[#8D6E63] font-bold">Qty</div>
              <div className="flex items-center bg-[#FBF4E1] border border-[#8D6E63]/20 rounded-full overflow-hidden">
                <button
                  onClick={dec}
                  disabled={qty <= 1}
                  aria-label="Decrease quantity"
                  data-testid={`${cardTestId}-qty-dec`}
                  className="w-10 h-10 flex items-center justify-center hover:bg-[#F6EBCB] disabled:opacity-40 transition"
                >
                  <Minus className="w-4 h-4 text-[#143C18]" />
                </button>
                <div className="w-10 text-center font-bold text-[#143C18] tabular-nums" data-testid={`${cardTestId}-qty-value`}>{qty}</div>
                <button
                  onClick={inc}
                  aria-label="Increase quantity"
                  data-testid={`${cardTestId}-qty-inc`}
                  className="w-10 h-10 flex items-center justify-center hover:bg-[#F6EBCB] transition"
                >
                  <Plus className="w-4 h-4 text-[#143C18]" />
                </button>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 grid grid-cols-1 sm:flex sm:flex-wrap gap-3">
            <button
              onClick={onBuyNow}
              disabled={!available || buying}
              data-testid={buyTestId}
              className="btn-primary !py-3.5 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {buying ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>
              ) : (
                <><ShoppingBag className="w-4 h-4" /> Buy Now · {money(Number(product.price.amount) * qty, product.currency)}</>
              )}
            </button>
            <button
              onClick={onAddToCart}
              disabled={!available || buying}
              data-testid={addTestId}
              className="btn-outline !py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" /> Add to Cart
            </button>
            {!available && (
              <a
                href={waUrl(notifyMsg || `Hi Rudrima! Please notify me when "${product.title}" is back in stock.`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`${cardTestId}-notify-btn`}
                className="btn-outline !py-3 !border-[#25D366] !text-[#0F5132] hover:!bg-[#25D366] hover:!text-white"
              >
                <MessageCircle className="w-4 h-4" /> Notify me
              </a>
            )}
          </div>
          <div className="mt-3 text-xs text-[#8D6E63] flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>Checkout, orders & inventory are handled securely by Shopify.</span>
            {detailsTo && (
              <Link to={detailsTo} data-testid={`${cardTestId}-details-link`} className="text-[#2E7D32] font-semibold inline-flex items-center gap-1 hover:gap-1.5 transition-all">
                {detailsLabel || "View details"} <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function shortTitle(t) {
  if (!t) return "item";
  return t.split(" - ")[0];
}

function ShopifyCardSkeleton({ reverse, testId }) {
  return (
    <div className="card-premium overflow-hidden animate-pulse" data-testid={testId ? `${testId}-skeleton` : "shopify-card-skeleton"}>
      <div className={`grid lg:grid-cols-2 ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
        <div className="bg-[#F6EBCB] min-h-[320px]" />
        <div className="p-6 lg:p-10 space-y-4">
          <div className="h-3 w-24 bg-[#F6EBCB] rounded" />
          <div className="h-8 w-3/4 bg-[#F6EBCB] rounded" />
          <div className="h-4 w-full bg-[#F6EBCB] rounded" />
          <div className="h-4 w-5/6 bg-[#F6EBCB] rounded" />
          <div className="pt-6 border-t border-[#8D6E63]/15 flex items-center justify-between">
            <div className="h-10 w-32 bg-[#F6EBCB] rounded" />
            <div className="h-10 w-24 bg-[#F6EBCB] rounded-full" />
          </div>
          <div className="flex gap-3">
            <div className="h-12 w-44 bg-[#F6EBCB] rounded-full" />
            <div className="h-12 w-36 bg-[#F6EBCB] rounded-full" />
          </div>
        </div>
      </div>
    </div>
  );
}
